import {
  DEFAULT_LANDING_PAGE_CONFIG,
  type LandingPageConfig,
  parseLandingPageConfig,
  resolveLandingPageLogoHeight,
} from '@provision/landing-page-utils';
import {
  type LandingPageBlockValues,
  type LandingPageSettingsValues,
} from 'src/pages/Settings/memberships/components/landing-page-tab/types';

function trimmed(value: string): string | undefined {
  return value.trim() || undefined;
}

function dropUndefined(record: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(record).filter(([, value]) => value !== undefined));
}

function serializeBackground(
  values: LandingPageBlockValues,
): Record<string, unknown> | undefined {
  const fill = values.backgroundFill;
  if (fill === 'image') {
    const imageUrl = trimmed(values.backgroundImageUrl);
    return imageUrl
      ? {
          fill,
          imageUrl,
          imageFit: values.backgroundImageFit,
          imageFocusX: values.backgroundImageFocusX,
          imageFocusY: values.backgroundImageFocusY,
        }
      : undefined;
  }

  const color = trimmed(values.backgroundColor);
  return fill === 'none' || !color ? undefined : { fill, color };
}

function serializeBlock(values: LandingPageBlockValues): Record<string, unknown> {
  const base = dropUndefined({
    id: values.id,
    type: values.type,
    background: serializeBackground(values),
    minHeight: values.minHeight || undefined,
  });

  switch (values.type) {
    case 'hero': {
      const video = trimmed(values.heroVideoUrl)
        ? dropUndefined({
            mediaUrl: values.heroVideoUrl.trim(),
            title: trimmed(values.heroVideoTitle),
            caption: trimmed(values.heroVideoCaption),
          })
        : undefined;

      return dropUndefined({
        ...base,
        headline: values.heroHeadline,
        subtext: trimmed(values.heroSubtext),
        mediaKind: values.heroMediaKind,
        video,
        image: trimmed(values.heroImageUrl) ? { url: values.heroImageUrl.trim() } : undefined,
        imageFit: values.heroImageFit,
        imageFocusX: values.heroImageFocusX,
        imageFocusY: values.heroImageFocusY,
      });
    }
    case 'content':
      return dropUndefined({
        ...base,
        kicker: trimmed(values.contentKicker),
        title: values.contentTitle,
        body: values.contentBody,
        imageUrl: trimmed(values.contentImageUrl),
        mediaPosition: values.contentMediaPosition,
        imageFit: values.contentImageFit,
        imageFocusX: values.contentImageFocusX,
        imageFocusY: values.contentImageFocusY,
      });
    case 'plans':
      return dropUndefined({
        ...base,
        kicker: trimmed(values.plansKicker),
        title: trimmed(values.plansTitle),
        subtext: trimmed(values.plansSubtext),
      });
    case 'closingCta':
      return dropUndefined({
        ...base,
        headline: values.ctaHeadline,
        subtext: trimmed(values.ctaSubtext),
        buttonLabel: values.ctaButtonLabel,
      });
    default:
      return base;
  }
}

// `logoSize` rides along from the stored config and `logoHeight` supersedes it, but only once it
// differs: a page saved for another reason keeps its settings byte-identical.
function serializeHeader(
  values: LandingPageSettingsValues,
  stored: LandingPageConfig['settings'],
): Record<string, unknown> | undefined {
  const writeHeight =
    stored.header?.logoHeight !== undefined ||
    values.logoHeight !== resolveLandingPageLogoHeight(stored);
  const header = dropUndefined({
    ...stored.header,
    backgroundColor: trimmed(values.headerBackgroundColor),
    textColor: trimmed(values.headerTextColor),
    logoUrl: trimmed(values.logoUrl),
    logoHeight: writeHeight ? values.logoHeight : undefined,
  });

  return Object.keys(header).length > 0 ? header : undefined;
}

function serializeSettings(
  values: LandingPageSettingsValues,
  stored: LandingPageConfig['settings'],
): Record<string, unknown> {
  const consultationUrl =
    values.ctaMode === 'consultation' ? trimmed(values.consultationUrl) : undefined;

  return {
    ...stored,
    showPricing: values.showPricing,
    cta: dropUndefined({
      ...stored.cta,
      mode: values.ctaMode,
      consultationUrl,
    }),
    header: serializeHeader(values, stored),
    colors: dropUndefined({
      ...stored.colors,
      primary: trimmed(values.primaryColor),
      secondary: trimmed(values.secondaryColor),
      accent: trimmed(values.accentColor),
    }),
  };
}

// Merging onto the stored config at every level carries `version` and any unknown keys through.
export function serializeLandingPageConfig({
  storedConfig,
  blocks,
  settings,
}: {
  storedConfig: string | null | undefined;
  blocks: readonly LandingPageBlockValues[];
  settings: LandingPageSettingsValues;
}): string {
  const base = parseLandingPageConfig(storedConfig) ?? DEFAULT_LANDING_PAGE_CONFIG;

  return JSON.stringify({
    ...base,
    blocks: blocks.map(serializeBlock),
    settings: serializeSettings(settings, base.settings),
  });
}
